import { useCallback, useRef } from "react"

import { OverrideMode, useVariantOverrides } from "./useVariantOverrides"
import { useVariants } from "./useVariants"
import { Client } from "../client"
import { getVariantFromFallback } from "../helpers/getVariantFromFallback"
import type { Fallback, Variant } from "../types"

type GetVariant<TPayload> = () => Variant<TPayload>

/**
 * Returns a function so that you can delay calling `Client.getVariant` until
 * absolutely necessary, since it automatically triggers an exposure event.
 */
export const useGetVariant = <TPayload>(name: string, fallback: Fallback<TPayload>): GetVariant<TPayload> => {
  // Make sure the component re-renders if the stored value changes
  useVariants(state => state.variants[name])

  /**
   * Store the fallback in a ref so that the latest value can be accessed in
   * the callback without updating the function reference on each render if
   * it's an object.
   */
  const fallbackRef = useRef(fallback)
  fallbackRef.current = fallback

  const overrideMode = useVariantOverrides.useMode()
  const override = useVariantOverrides(state => state.overrides[name])

  return useCallback(() => {
    const fallbackValue = getVariantFromFallback(fallbackRef.current)

    if (overrideMode === OverrideMode.OverridesOnly) {
      return override ?? fallbackValue
    }

    return override ?? Client.getVariant(name, fallbackValue)
  }, [name, override, overrideMode])
}
